import React from 'react';
import axios from 'axios';
import { translate } from 'react-i18next';
import CompaniesTab from './CompaniesTab';
import WorkshopsTab from './WorkshopsTab';
import EzyvinTab from './EzyvinTab';
import {
  buildQuery, formatDate, parseQuery, weekNumber, weekRange,
} from './engagementHelpers';

/*
 * Staff engagement page (OVIO-110, design docs/specs/design/ovio-105-110-staff-engagement.md).
 * One page, one tab per ticket; every tab shares the week axis, the group filter (kept in the query
 * string so a group can be bookmarked), the CSV link and the history-start note.
 */
const BASE = '/uiapi/1/staff/engagement/';
// Each tab has its own address (bookmarkable with its filter), endpoint, CSV and history note.
const usageSubtitle = (data, t) => t('subtitle', { range: weekRange(data.weeks), latest: weekNumber(data.latest_week) });
const TABS = [
  {
    key: 'companies', path: '/staff_settings/engagement', label: 'tabCompanies', Body: CompaniesTab,
    allLabel: 'groupAll', note: 'dataStarts', noDataLegend: 'legendNoData', subtitle: usageSubtitle,
    api: () => 'companies/', csv: () => 'companies/export_csv/',
  },
  {
    key: 'workshops', path: '/staff_settings/engagement/korjaamot', label: 'tabWorkshops', Body: WorkshopsTab,
    allLabel: 'groupAllWorkshops', note: 'dataStartsWorkshops', noDataLegend: 'legendNoPress', subtitle: usageSubtitle,
    api: () => 'workshops/', csv: data => (data && data.pilot ? 'workshops/pilot/export_csv/' : 'workshops/export_csv/'),
  },
  {
    // OVIO-103: no usage-history note; one workshop's month report when the address names it.
    key: 'ezyvin', path: '/staff_settings/engagement/valmistajahaut', label: 'tabEzyvin', Body: EzyvinTab,
    allLabel: 'groupAllEzyvin', note: null, noDataLegend: null,
    subtitle: (data, t) => t('subtitleEzyvin', { month: data.month }),
    api: query => (query.workshop ? 'ezyvin/' + encodeURIComponent(query.workshop) + '/' : 'ezyvin/'),
    csv: (data, query) => (query.workshop ? 'ezyvin/' + encodeURIComponent(query.workshop) + '/export_csv/' : 'ezyvin/export_csv/'),
  },
];
const BLUE = '#0095DA';
// Typing waits this long before the address (and so the heavy queries) change.
const TYPING_PAUSE_MS = 300;

const BOLD = 'TitilliumWeb-SemiBold';
const box = {
  height: 44, border: '1px solid #C9CED4', borderRadius: 4, fontSize: 16, padding: '0 12px', background: '#fff',
  color: '#222', boxSizing: 'border-box', fontFamily: 'inherit',
};

export class StaffEngagementView extends React.Component {
  constructor(props) {
    super(props);
    const query = parseQuery(props.location.search);
    // The inputs are controlled; the address stays the one truth and resets them on back/forward.
    this.state = {
      data: null, loadedFor: null, error: false, open: false, ownList: false, pending: false, q: query.q, ids: query.ids,
    };
    this.seq = 0;
    this.timer = null;
  }

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.search !== this.props.location.search
        || prevProps.location.pathname !== this.props.location.pathname) {
      clearTimeout(this.timer); // a navigation supersedes a draft still waiting for its pause
      const query = this.query();
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        q: query.q, ids: query.ids, ownList: false, pending: false,
      });
      this.load();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timer);
    this.seq += 1; // a reply still on its way is dropped
  }

  tab() {
    const path = (this.props.location.pathname || '').replace(/\/$/, '');
    return TABS.filter(tab => tab.path === path)[0] || TABS[0];
  }

  // What a loaded answer belongs to: the tab and the address.
  target() {
    return this.tab().key + this.search();
  }

  query() {
    return parseQuery(this.props.location.search);
  }

  // The address carried to another tab: the pilot week means nothing on Yritykset.
  searchFor(tab) {
    const parts = this.search().replace(/^\?/, '').split('&')
      .filter(part => part && (tab.key === 'workshops' || part.indexOf('start_week=') !== 0));
    return parts.length ? '?' + parts.join('&') : '';
  }

  // The answer for this tab and address, or null while it loads.
  current() {
    return this.state.loadedFor === this.target() ? this.state.data : null;
  }

  // The address goes to the server as it is: a bad bookmark must be refused there, not quietly
  // widened here (buildQuery only writes the addresses this page makes itself).
  search() {
    return this.props.location.search || '';
  }

  load() {
    this.seq += 1;
    const seq = this.seq;
    const search = this.search();
    const target = this.target();
    this.setState({ error: false });
    axios.get(BASE + this.tab().api(this.query()) + search)
      .then((response) => {
        if (seq === this.seq) {
          this.setState({ data: response.data, loadedFor: target, error: false });
        }
      })
      .catch((err) => {
        if (seq === this.seq) {
          // 400 = the group in the address is not valid (an old bookmark), not a failure of the page.
          const bad = err && err.response && err.response.status === 400;
          this.setState({ error: bad ? 'badGroup' : 'error', loadedFor: null });
        }
      });
  }

  typed(field, value) {
    this.setState({ [field]: value, pending: true });
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.commitDraft(), TYPING_PAUSE_MS);
  }

  // The whole draft (search and id list) goes into the address at once, so no edit is lost.
  draft() {
    const query = this.query();
    const changes = { q: this.state.q.trim() };
    if (query.group === 'ids' || this.state.ownList) {
      changes.group = 'ids';
      changes.ids = this.state.ids.replace(/\s/g, '');
    }
    // A new or changed id list is a new group: it does not inherit the previous pilot week.
    if (this.state.ownList || (changes.group === 'ids' && changes.ids !== query.ids)) {
      changes.start_week = '';
    }
    return changes;
  }

  // A start-week change takes any typed but not yet committed filter along with it.
  startWeek(week) {
    clearTimeout(this.timer);
    this.setState({ pending: false });
    this.go({ ...this.draft(), start_week: week });
  }

  commitDraft() {
    this.setState({ pending: false });
    this.go(this.draft(), true);
  }

  go(changes, replace) {
    const query = this.query();
    const next = { ...query, ...changes };
    // A new selection may not hold the open workshop (its report would answer 404): it closes it.
    if (['q', 'group', 'chain', 'ids'].some(key => (next[key] || '') !== (query[key] || ''))) {
      next.workshop = '';
    }
    const search = buildQuery(next);
    if (replace) {
      this.props.history.replace({ search });
    } else {
      this.props.history.push({ search });
    }
  }

  choose(option) {
    clearTimeout(this.timer); // the choice carries the typed search with it
    this.setState({ open: false, ownList: option.group === 'ids', pending: false });
    if (option.group !== 'ids') {
      this.go({
        group: option.group, chain: option.chain || '', ids: '', q: this.state.q.trim(), start_week: '',
      });
    }
  }

  options() {
    const { t } = this.props;
    const groups = (this.state.data && this.state.data.groups) || { chains: [], pilot: 0 };
    const tab = this.tab();
    return [{ group: 'all', label: t(tab.allLabel), hint: tab.key === 'workshops' ? String(groups.all || '') : '' }]
      .concat(groups.chains.map(chain => ({
        group: 'chain', chain: String(chain.id), label: t('groupChain', { name: chain.name }), hint: String(chain.workshops),
      })))
      .concat([
        { group: 'pilot', label: t('groupPilot'), hint: String(groups.pilot) },
        { group: 'ids', label: t('groupIds'), hint: '?ids=12,57,301' },
      ]);
  }

  renderFilter() {
    const { t } = this.props;
    const query = this.query();
    const options = this.options();
    const current = options.filter(o => o.group === query.group && (o.group !== 'chain' || o.chain === query.chain))[0];
    const showIds = query.group === 'ids' || this.state.ownList;
    let currentLabel = current ? current.label : t(this.tab().allLabel);
    if (showIds) {
      currentLabel = t('groupIds');
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', marginTop: 22, position: 'relative' }}>
        <button
          type='button'
          data-group-toggle
          aria-expanded={this.state.open}
          onClick={() => this.setState(state => ({ open: !state.open }))}
          style={{
            ...box, width: 290, flex: '0 0 auto', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center',
            borderColor: this.state.open ? BLUE : '#C9CED4', borderWidth: this.state.open ? 2 : 1,
          }}
        >
          <span style={{ flex: 1 }}>{t('groupLabel', { name: currentLabel })}</span>
          <span style={{ fontSize: 10, color: '#4A5560' }}>▼</span>
        </button>
        {this.state.open && (
          <div
            onKeyDown={(event) => { if (event.key === 'Escape') { this.setState({ open: false }); } }}
            style={{
              position: 'absolute', top: 52, left: 0, width: 330, background: '#fff', zIndex: 10,
              boxShadow: '0 4px 14px rgba(0,0,0,0.15)', border: '1px solid #E3E6EA',
            }}
          >
            {options.map(option => (
              <button
                type='button'
                data-group-option
                key={option.group + (option.chain || '')}
                aria-pressed={option === current}
                onClick={() => this.choose(option)}
                style={{
                  display: 'flex', alignItems: 'center', width: '100%', border: 'none', textAlign: 'left', color: '#222',
                  height: 44, padding: '0 14px', cursor: 'pointer', fontSize: 15,
                  background: option === current ? '#E3F2FB' : '#fff', fontFamily: option === current ? BOLD : 'inherit',
                }}
              >
                <span style={{ flex: 1 }}>{option.label}</span>
                <span style={{ fontSize: 13, color: '#5F6B76', fontFamily: 'TitilliumWeb-Regular' }}>{option.hint}</span>
              </button>
            ))}
          </div>
        )}
        {showIds && (
          <input
            name='ids'
            aria-label={t('groupIds')}
            placeholder={t('idsPlaceholder')}
            value={this.state.ids}
            onChange={event => this.typed('ids', event.target.value)}
            style={{ ...box, width: 170, marginLeft: 12 }}
          />
        )}
        <input
          type='search'
          aria-label={t('search')}
          placeholder={t('search')}
          value={this.state.q}
          onChange={event => this.typed('q', event.target.value)}
          style={{ ...box, flex: '0 1 240px', minWidth: 120, marginLeft: 12 }}
        />
        <span style={{ flex: 1 }} />
        {this.renderCsv()}
      </div>
    );
  }

  renderCsv() {
    const { t } = this.props;
    const style = {
      background: BLUE, color: '#fff', height: 44, lineHeight: '44px', padding: '0 18px', borderRadius: 4,
      fontSize: 16, fontFamily: BOLD, textDecoration: 'none', whiteSpace: 'nowrap', marginLeft: 12,
    };
    if (this.state.pending || !this.current()) {
      // Typed filters are not in the address yet, or the answer that decides the file (list or pilot)
      // has not come: a download now could export the wrong selection or the wrong file.
      return <span data-csv aria-disabled='true' style={{ ...style, opacity: 0.5 }}>{t('csv')}</span>;
    }
    return (
      <a data-csv download href={BASE + this.tab().csv(this.current(), this.query()) + this.search()} style={style}>
        {t('csv')}
      </a>
    );
  }

  renderNote(data) {
    const { t } = this.props;
    let text = null;
    if (!this.tab().note) {
      return null;
    }
    if (!data.data_start) {
      text = t('noData');
    } else if (data.history_short) {
      text = t(this.tab().note, { date: formatDate(data.data_start), week: weekNumber(data.data_start_week) });
    }
    if (!text) {
      return null;
    }
    return (
      <div
        role='note'
        style={{
          marginTop: 22, padding: '12px 16px', border: '1px solid #F2C14E', background: '#FFF8E6', borderRadius: 4,
          fontSize: 14, lineHeight: '22px', color: '#222',
        }}
      >
        {text}
      </div>
    );
  }

  renderLegend(data) {
    const { t } = this.props;
    if (!this.tab().noDataLegend || !data.data_start || !data.history_short) {
      return null;
    }
    const swatch = color => ({
      display: 'inline-block', width: 12, height: 12, background: color, marginRight: 8, verticalAlign: 'middle',
    });
    return (
      <div style={{ fontSize: 13, color: '#4A5560', marginTop: 18 }}>
        <span style={{ marginRight: 22 }}><span style={swatch('#E3E6EA')} />{t(this.tab().noDataLegend)}</span>
        <span style={{ marginRight: 22 }}><span style={swatch('#B8BEC6')} />{t('legendZero')}</span>
        <span><span style={swatch(BLUE)} />{t('legendCount')}</span>
      </div>
    );
  }

  render() {
    const { t } = this.props;
    const { error } = this.state;
    // Figures are shown only for the selection they were fetched for; the last answer's group list
    // stays usable while a new selection loads or after it failed.
    const data = !error ? this.current() : null;
    const Body = this.tab().Body;
    return (
      <div style={{
        background: '#fff', padding: '30px 32px 30px', borderRadius: 4, fontFamily: 'TitilliumWeb-Regular', color: '#222',
      }}
      >
        <h1 style={{ fontSize: 34, fontWeight: 'normal', fontFamily: 'TitilliumWeb-Regular', color: '#222', margin: 0 }}>{t('title')}</h1>
        {data && (
          <div style={{ fontSize: 14, color: '#5F6B76', marginTop: 8 }}>
            {this.tab().subtitle(data, t)}
          </div>
        )}
        <div style={{ display: 'flex', borderBottom: '1px solid #E3E6EA', marginTop: 22 }}>
          {TABS.map((tab) => {
            const active = tab === this.tab();
            return (
              <a
                key={tab.key}
                data-tab
                href={tab.path + this.searchFor(tab)}
                aria-current={active ? 'page' : undefined}
                onClick={(event) => {
                  event.preventDefault();
                  this.props.history.push({ pathname: tab.path, search: this.searchFor(tab) });
                }}
                style={{
                  fontSize: 18, padding: '0 0 8px', marginRight: 32, textDecoration: 'none', marginBottom: -1,
                  color: active ? BLUE : '#5F6B76', fontFamily: active ? BOLD : 'inherit',
                  borderBottom: active ? '3px solid ' + BLUE : '3px solid transparent',
                }}
              >
                {t(tab.label)}
              </a>
            );
          })}
        </div>
        {this.renderFilter()}
        {error && <div role='alert' style={{ marginTop: 22, color: '#C62828', fontSize: 15 }}>{t(error)}</div>}
        {!error && !data && <div style={{ marginTop: 22, color: '#5F6B76' }}>{t('loading')}</div>}
        {data && this.renderNote(data)}
        {data && (
          <Body
            data={data}
            t={t}
            onStartWeek={week => this.startWeek(week)}
            onWorkshop={workshop => this.go({ workshop })}
            onPeriod={period => this.go({ period: period === 'month' ? '' : period })}
          />
        )}
        {data && this.renderLegend(data)}
      </div>
    );
  }
}

export default translate('StaffEngagementView')(StaffEngagementView);
